import { useState, useEffect, useMemo, useRef } from "react";
import {
  motion,
  AnimatePresence,
  useDragControls,
  useMotionValue,
} from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { Checkbox } from "../../components/ui/checkbox";
import useJobApplications from "../../api/jobApplication";
import useItems from "../../api/items";
import { smartCompressImage } from "../../lib/imageCompression";
import {
  Briefcase,
  Users,
  UserCheck,
  UserX,
  Star,
  Search,
  Filter,
  Download,
  Plus,
  Trash2,
  Eye,
  X,
  Check,
  AlertCircle,
  Clock,
  Mail,
  Phone,
  Globe,
  FileText,
  ChevronDown,
  ChevronUp,
  Edit,
  ToggleLeft,
  ToggleRight,
  Upload,
  Image,
  Video,
  ExternalLink,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

// ─── Constants ─────────────────────────────────────────────

const getStatusConfig = (isDark) => ({
  new: {
    label: "Nieuw",
    color: isDark ? "bg-blue-500/15 text-blue-400" : "bg-blue-50 text-blue-700",
    icon: <AlertCircle className="h-3 w-3" />,
  },
  reviewed: {
    label: "Bekeken",
    color: isDark
      ? "bg-purple-500/15 text-purple-400"
      : "bg-purple-50 text-purple-700",
    icon: <Eye className="h-3 w-3" />,
  },
  interview: {
    label: "Interview",
    color: isDark
      ? "bg-amber-500/15 text-amber-400"
      : "bg-amber-50 text-amber-700",
    icon: <Clock className="h-3 w-3" />,
  },
  accepted: {
    label: "Aangenomen",
    color: isDark
      ? "bg-emerald-500/15 text-emerald-400"
      : "bg-emerald-50 text-emerald-700",
    icon: <UserCheck className="h-3 w-3" />,
  },
  rejected: {
    label: "Afgewezen",
    color: isDark ? "bg-red-500/15 text-red-400" : "bg-red-50 text-red-700",
    icon: <UserX className="h-3 w-3" />,
  },
});

const JOB_TYPES = [
  "Full-Time",
  "Full-Time / Freelancer",
  "Part-Time",
  "Freelancer",
  "Intern",
];

const S3_BASE_URL = process.env.REACT_APP_S3_BASE_URL;

const getMediaUrl = (key) => {
  if (!key) return null;
  if (key.startsWith("http") || key.startsWith("data:")) return key;
  return `${S3_BASE_URL}/${key}`;
};

const isVideo = (url) => {
  if (!url) return false;
  return /\.(mp4|webm|mov|avi|mkv)$/i.test(url);
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("nl-NL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const formatDateShort = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("nl-NL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

// ─── Star Rating Component ────────────────────────────────

const StarRating = ({ rating, onRate, size = "sm" }) => {
  const starSize = size === "sm" ? "h-4 w-4" : "h-5 w-5";
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onRate && onRate(star === rating ? 0 : star)}
          className={`${onRate ? "cursor-pointer hover:scale-110" : "cursor-default"} transition-transform`}
        >
          <Star
            className={`${starSize} ${
              star <= rating
                ? "fill-yellow-400 text-yellow-400"
                : "text-gray-300"
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────

const JobApplicationsManager = ({ targetCompanyId }) => {
  const { company } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const STATUS_CONFIG = getStatusConfig(isDark);
  const {
    getApplications,
    getApplicationStats,
    updateApplication,
    deleteApplication,
    exportApplications,
    getListings,
    createListing,
    updateListing,
    deleteListing,
  } = useJobApplications(targetCompanyId);
  const { uploadImage, uploadMedia } = useItems();
  const mediaInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  // State
  const [applications, setApplications] = useState([]);
  const [listings, setListings] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    new: 0,
    reviewed: 0,
    interview: 0,
    accepted: 0,
    rejected: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [jobFilter, setJobFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // UI state
  const [activeTab, setActiveTab] = useState("applications"); // 'applications' | 'listings'
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Listing form
  const [listingDialogOpen, setListingDialogOpen] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [listingForm, setListingForm] = useState({
    title: "",
    type: "Full-Time",
    shortDescription: "",
    longDescription: "",
    mediaUrl: "",
    isActive: true,
  });

  // Delete confirm
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleteType, setDeleteType] = useState(null); // 'application' | 'listing'

  // Sheet / mobile slide-up
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 640 : false,
  );
  const sheetRef = useRef(null);
  const sheetContentRef = useRef(null);
  const sheetDragControls = useDragControls();
  const sheetY = useMotionValue(0);
  const isSheetScrolledToTop = useRef(true);
  const sheetTouchStartY = useRef(null);
  const sheetDragging = useRef(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Attach native touch listeners so we can preventDefault scroll when pulling down from top
  useEffect(() => {
    // Reset state for newly opened sheet
    isSheetScrolledToTop.current = true;
    sheetTouchStartY.current = null;
    sheetDragging.current = false;

    const el = sheetContentRef.current;
    if (!el || !isMobile) return;

    const onScroll = () => {
      isSheetScrolledToTop.current = el.scrollTop <= 0;
    };

    const onTouchStart = (e) => {
      sheetTouchStartY.current = e.touches[0].clientY;
      sheetDragging.current = false;
    };

    const onTouchMove = (e) => {
      if (sheetDragging.current) {
        e.preventDefault();
        return;
      }
      if (sheetTouchStartY.current === null) return;

      const currentY = e.touches[0].clientY;
      const deltaY = currentY - sheetTouchStartY.current;

      // Pulling down while at scroll top → start drag
      if (deltaY > 8 && isSheetScrolledToTop.current) {
        sheetDragging.current = true;
        e.preventDefault();
        // Create a synthetic PointerEvent for framer-motion dragControls
        const touch = e.touches[0];
        const syntheticEvent = new PointerEvent("pointerdown", {
          clientX: touch.clientX,
          clientY: touch.clientY,
          pointerId: 1,
          pointerType: "touch",
          bubbles: true,
        });
        sheetDragControls.start(syntheticEvent);
      }
    };

    const onTouchEnd = () => {
      sheetTouchStartY.current = null;
      sheetDragging.current = false;
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener("scroll", onScroll);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [
    isMobile,
    sheetDragControls,
    detailOpen,
    listingDialogOpen,
    deleteConfirmId,
  ]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [appsData, statsData, listingsData] = await Promise.all([
        getApplications(),
        getApplicationStats(),
        getListings(),
      ]);
      setApplications(appsData || []);
      setStats(
        statsData || {
          total: 0,
          new: 0,
          reviewed: 0,
          interview: 0,
          accepted: 0,
          rejected: 0,
        },
      );
      setListings(listingsData || []);
    } catch (err) {
      console.error("Error fetching job data:", err);
      setError("Er is een fout opgetreden bij het ophalen van de gegevens.");
    } finally {
      setLoading(false);
    }
  };

  // Filter applications
  const filteredApplications = useMemo(() => {
    let filtered = applications;

    if (statusFilter !== "all") {
      filtered = filtered.filter((a) => a.status === statusFilter);
    }

    if (jobFilter !== "all") {
      filtered = filtered.filter((a) => a.job_title === jobFilter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (a) =>
          a.first_name?.toLowerCase().includes(q) ||
          a.last_name?.toLowerCase().includes(q) ||
          a.email?.toLowerCase().includes(q) ||
          a.job_title?.toLowerCase().includes(q),
      );
    }

    return filtered;
  }, [applications, statusFilter, jobFilter, searchQuery]);

  // Unique job titles for filter
  const uniqueJobTitles = useMemo(() => {
    const titles = new Set(applications.map((a) => a.job_title));
    return Array.from(titles).sort();
  }, [applications]);

  // ─── Handlers ─────────────────────────────────────────────

  const handleStatusChange = async (applicationId, newStatus) => {
    try {
      const updated = await updateApplication(applicationId, {
        status: newStatus,
      });
      setApplications((prev) =>
        prev.map((a) => (a.id === applicationId ? updated : a)),
      );
      if (selectedApplication?.id === applicationId) {
        setSelectedApplication(updated);
      }
      // Refresh stats
      const newStats = await getApplicationStats();
      setStats(newStats);
      showSuccess("Status bijgewerkt");
    } catch (err) {
      setError("Kon status niet bijwerken");
    }
  };

  const handleRatingChange = async (applicationId, rating) => {
    try {
      const updated = await updateApplication(applicationId, { rating });
      setApplications((prev) =>
        prev.map((a) => (a.id === applicationId ? updated : a)),
      );
      if (selectedApplication?.id === applicationId) {
        setSelectedApplication(updated);
      }
    } catch (err) {
      setError("Kon beoordeling niet bijwerken");
    }
  };

  const handleNotesChange = async (applicationId, notes) => {
    try {
      const updated = await updateApplication(applicationId, { notes });
      setApplications((prev) =>
        prev.map((a) => (a.id === applicationId ? updated : a)),
      );
      if (selectedApplication?.id === applicationId) {
        setSelectedApplication(updated);
      }
      showSuccess("Notities opgeslagen");
    } catch (err) {
      setError("Kon notities niet opslaan");
    }
  };

  const handleDeleteApplication = async (id) => {
    try {
      await deleteApplication(id);
      setApplications((prev) => prev.filter((a) => a.id !== id));
      const newStats = await getApplicationStats();
      setStats(newStats);
      setDeleteConfirmId(null);
      setDeleteType(null);
      if (selectedApplication?.id === id) {
        setDetailOpen(false);
        setSelectedApplication(null);
      }
      showSuccess("Sollicitatie verwijderd");
    } catch (err) {
      setError("Kon sollicitatie niet verwijderen");
    }
  };

  const handleExport = async () => {
    try {
      await exportApplications({
        status: statusFilter !== "all" ? statusFilter : undefined,
        jobListingId: jobFilter !== "all" ? jobFilter : undefined,
        search: searchQuery || undefined,
      });
      showSuccess("Export gedownload");
    } catch (err) {
      setError("Export mislukt");
    }
  };

  // ─── Listing Handlers ─────────────────────────────────────

  const openListingForm = (listing = null) => {
    if (listing) {
      setEditingListing(listing);
      setListingForm({
        title: listing.title,
        type: listing.type || "Full-Time",
        shortDescription: listing.short_description || "",
        longDescription: listing.long_description || "",
        mediaUrl: listing.media_url || "",
        isActive:
          listing.is_active !== undefined ? Boolean(listing.is_active) : true,
      });
    } else {
      setEditingListing(null);
      setListingForm({
        title: "",
        type: "Full-Time",
        shortDescription: "",
        longDescription: "",
        mediaUrl: "",
        isActive: true,
      });
    }
    setListingDialogOpen(true);
  };

  // ─── Media Upload Handler ─────────────────────────────────
  const handleMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const isVideoFile = file.type.startsWith("video/");
      let mediaKey;

      if (isVideoFile) {
        const resp = await uploadMedia(file);
        mediaKey =
          resp?.data?.mediaKey || resp?.data?.imageKey || resp?.data?.imageId;
      } else {
        const compressedDataUrl = await smartCompressImage(file);
        const resp = await uploadImage(compressedDataUrl);
        mediaKey = resp?.data?.imageKey || resp?.data?.imageId;
      }

      if (mediaKey) {
        setListingForm((prev) => ({ ...prev, mediaUrl: mediaKey }));
      }
    } catch (error) {
      console.error("Media upload failed:", error);
      setError("Media uploaden mislukt");
    } finally {
      setUploading(false);
      if (mediaInputRef.current) mediaInputRef.current.value = "";
    }
  };

  const handleListingSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingListing) {
        const updated = await updateListing(editingListing.id, listingForm);
        setListings((prev) =>
          prev.map((l) => (l.id === editingListing.id ? updated : l)),
        );
        showSuccess("Vacature bijgewerkt");
      } else {
        const created = await createListing(listingForm);
        setListings((prev) => [created, ...prev]);
        showSuccess("Vacature aangemaakt");
      }
      setListingDialogOpen(false);
    } catch (err) {
      setError("Kon vacature niet opslaan");
    }
  };

  const handleToggleListingActive = async (listing) => {
    try {
      const updated = await updateListing(listing.id, {
        isActive: !listing.is_active,
      });
      setListings((prev) =>
        prev.map((l) => (l.id === listing.id ? updated : l)),
      );
      showSuccess(
        updated.is_active ? "Vacature geactiveerd" : "Vacature gedeactiveerd",
      );
    } catch (err) {
      setError("Kon status niet wijzigen");
    }
  };

  const handleDeleteListing = async (id) => {
    try {
      await deleteListing(id);
      setListings((prev) => prev.filter((l) => l.id !== id));
      setDeleteConfirmId(null);
      setDeleteType(null);
      showSuccess("Vacature verwijderd");
    } catch (err) {
      setError("Kon vacature niet verwijderen");
    }
  };

  const showSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(""), 3000);
  };

  // ─── Render ───────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div
          className={`w-8 h-8 rounded-full border-2 animate-spin ${isDark ? "border-white/[0.08] border-t-white/40" : "border-gray-200 border-t-gray-600"}`}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: "Totaal", value: stats.total, icon: Users },
          {
            label: "Nieuw",
            value: stats.new,
            icon: AlertCircle,
            dot: isDark ? "bg-blue-400" : "bg-blue-500",
          },
          {
            label: "Bekeken",
            value: stats.reviewed,
            icon: Eye,
            dot: isDark ? "bg-purple-400" : "bg-purple-500",
          },
          {
            label: "Interview",
            value: stats.interview,
            icon: Clock,
            dot: isDark ? "bg-amber-400" : "bg-amber-500",
          },
          {
            label: "Aangenomen",
            value: stats.accepted,
            icon: UserCheck,
            dot: isDark ? "bg-emerald-400" : "bg-emerald-500",
          },
          {
            label: "Afgewezen",
            value: stats.rejected,
            icon: UserX,
            dot: isDark ? "bg-red-400" : "bg-red-500",
          },
        ].map((stat) => (
          <div
            key={stat.label}
            className={`rounded-xl p-3 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
          >
            <div className="flex items-center gap-1.5 mb-1.5">
              <stat.icon
                className={`h-3 w-3 ${isDark ? "text-gray-600" : "text-gray-400"}`}
              />
              <span
                className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                {stat.label}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {stat.dot && (
                <div className={`h-2 w-2 rounded-full ${stat.dot}`} />
              )}
              <span
                className={`text-xl font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
              >
                {stat.value}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Header + Tabs */}
      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-center flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <h1
              className={`text-[15px] font-semibold tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}
            >
              Sollicitaties
            </h1>

            {/* Tab Toggle */}
            <div
              className={`flex rounded-lg p-0.5 ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
            >
              {[
                { key: "applications", label: "Sollicitanten", icon: Users },
                { key: "listings", label: "Vacatures", icon: Briefcase },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-3 py-1.5 text-[12px] font-medium rounded-md transition-colors inline-flex items-center gap-1.5 ${
                    activeTab === tab.key
                      ? isDark
                        ? "bg-white/[0.08] text-white"
                        : "bg-white text-gray-900 shadow-sm"
                      : isDark
                        ? "text-gray-500 hover:text-gray-300"
                        : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <tab.icon className="h-3.5 w-3.5" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            {activeTab === "applications" && (
              <button
                onClick={handleExport}
                className={`h-9 px-4 rounded-lg text-[13px] font-medium inline-flex items-center gap-2 border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
              >
                <Download className="h-3.5 w-3.5" /> Exporteer CSV
              </button>
            )}
            {activeTab === "listings" && (
              <button
                onClick={() => openListingForm()}
                className={`h-9 px-4 rounded-lg text-[13px] font-medium inline-flex items-center gap-2 transition-colors ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
              >
                <Plus className="h-3.5 w-3.5" /> Nieuwe vacature
              </button>
            )}
          </div>
        </div>

        {/* Notifications */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              <div
                className={`flex items-center justify-between gap-2 px-4 py-3 rounded-xl text-[13px] ${isDark ? "bg-red-500/10 border border-red-500/20 text-red-400" : "bg-red-50 border border-red-200 text-red-700"}`}
              >
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
                <button onClick={() => setError("")} className="flex-shrink-0">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </motion.div>
          )}
          {success && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              <div
                className={`flex items-center gap-2 px-4 py-3 rounded-xl text-[13px] ${isDark ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-400" : "bg-emerald-50 border border-emerald-200 text-emerald-700"}`}
              >
                <Check className="h-3.5 w-3.5 flex-shrink-0" />
                <span>{success}</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ─── Applications Tab ─────────────────────────────── */}
      {activeTab === "applications" && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-4"
        >
          {/* Filters */}
          <div
            className={`flex flex-wrap items-center gap-2 p-3 rounded-xl ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
          >
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search
                className={`absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
              />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Zoek op naam, email..."
                className={`w-full h-9 pl-9 pr-3 rounded-lg text-[13px] outline-none transition-colors ${isDark ? "bg-white/[0.04] border border-white/[0.06] text-white placeholder-gray-600 focus:border-white/[0.12]" : "bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
              />
            </div>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger
                className={`w-[160px] h-9 rounded-lg text-[13px] ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-gray-50 border-gray-200 text-gray-900"}`}
              >
                <Filter className="h-3.5 w-3.5 mr-1.5" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent
                className={isDark ? "bg-[#1a1a1a] border-white/[0.08]" : ""}
              >
                <SelectItem value="all">Alle statussen</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                  <SelectItem key={key} value={key}>
                    {cfg.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={jobFilter} onValueChange={setJobFilter}>
              <SelectTrigger
                className={`w-[180px] h-9 rounded-lg text-[13px] ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-gray-50 border-gray-200 text-gray-900"}`}
              >
                <Briefcase className="h-3.5 w-3.5 mr-1.5" />
                <SelectValue placeholder="Functie" />
              </SelectTrigger>
              <SelectContent
                className={isDark ? "bg-[#1a1a1a] border-white/[0.08]" : ""}
              >
                <SelectItem value="all">Alle functies</SelectItem>
                {uniqueJobTitles.map((title) => (
                  <SelectItem key={title} value={title}>
                    {title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <span
              className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
            >
              {filteredApplications.length} resultaat
              {filteredApplications.length !== 1 ? "en" : ""}
            </span>
          </div>

          {/* Applications List */}
          {applications.length === 0 ? (
            <div
              className={`rounded-xl p-12 text-center ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
            >
              <div
                className={`w-12 h-12 mx-auto mb-4 rounded-full flex items-center justify-center ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
              >
                <Users
                  className={`h-5 w-5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                />
              </div>
              <h3
                className={`text-[14px] font-semibold mb-1 ${isDark ? "text-white" : "text-gray-900"}`}
              >
                Nog geen sollicitaties
              </h3>
              <p
                className={`text-[13px] max-w-md mx-auto ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                Er zijn nog geen sollicitaties binnengekomen. Zodra iemand
                solliciteert via je website, verschijnt de sollicitatie hier.
              </p>
            </div>
          ) : filteredApplications.length === 0 ? (
            <div
              className={`rounded-xl p-8 text-center ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
            >
              <div
                className={`w-10 h-10 mx-auto mb-3 rounded-full flex items-center justify-center ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
              >
                <Filter
                  className={`h-4 w-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                />
              </div>
              <p
                className={`text-[13px] mb-3 ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                Geen sollicitaties gevonden met de huidige filters.
              </p>
              <button
                onClick={() => {
                  setStatusFilter("all");
                  setJobFilter("all");
                  setSearchQuery("");
                }}
                className={`h-8 px-3 rounded-lg text-[12px] font-medium border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
              >
                Filters wissen
              </button>
            </div>
          ) : (
            <>
              {/* Mobile compact cards */}
              <div className="sm:hidden space-y-3">
                {filteredApplications.map((app, i) => {
                  const STATUS_CONFIG = getStatusConfig(isDark);
                  const statusCfg =
                    STATUS_CONFIG[app.status] || STATUS_CONFIG.new;
                  return (
                    <motion.div
                      key={app.id}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.02 }}
                      className={`rounded-xl overflow-hidden ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
                    >
                      <div className="p-3 flex items-center gap-3">
                        <div
                          className={`h-11 w-11 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
                        >
                          <Users
                            className={`h-5 w-5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3
                              className={`text-[13px] font-semibold truncate ${isDark ? "text-white" : "text-gray-900"}`}
                            >
                              {app.first_name} {app.last_name}
                            </h3>
                            <span
                              className={`${statusCfg.color} px-2 py-0.5 rounded-full text-[11px] font-medium inline-flex items-center gap-1 flex-shrink-0`}
                            >
                              {statusCfg.icon}
                              {statusCfg.label}
                            </span>
                          </div>
                          <span
                            className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                          >
                            {app.job_title}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => {
                              setSelectedApplication(app);
                              setDetailOpen(true);
                            }}
                            className={`h-8 w-8 rounded-lg inline-flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"}`}
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setDeleteConfirmId(app.id);
                              setDeleteType("application");
                            }}
                            className={`h-8 w-8 rounded-lg inline-flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-red-400 hover:bg-red-500/10" : "text-gray-400 hover:text-red-600 hover:bg-red-50"}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* Desktop detailed cards */}
              <div className="hidden sm:block space-y-2">
                {filteredApplications.map((app) => (
                  <ApplicationCard
                    key={app.id}
                    application={app}
                    isDark={isDark}
                    onStatusChange={handleStatusChange}
                    onRatingChange={handleRatingChange}
                    onView={() => {
                      setSelectedApplication(app);
                      setDetailOpen(true);
                    }}
                    onDelete={() => {
                      setDeleteConfirmId(app.id);
                      setDeleteType("application");
                    }}
                  />
                ))}
              </div>
            </>
          )}
        </motion.div>
      )}

      {/* ─── Listings Tab ─────────────────────────────────── */}
      {activeTab === "listings" && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-2"
        >
          {listings.length === 0 ? (
            <div
              className={`rounded-xl p-12 text-center ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
            >
              <div
                className={`w-12 h-12 mx-auto mb-4 rounded-full flex items-center justify-center ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
              >
                <Briefcase
                  className={`h-5 w-5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                />
              </div>
              <h3
                className={`text-[14px] font-semibold mb-1 ${isDark ? "text-white" : "text-gray-900"}`}
              >
                Nog geen vacatures
              </h3>
              <p
                className={`text-[13px] mb-5 max-w-md mx-auto ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                Maak je eerste vacature aan om sollicitanten te ontvangen.
              </p>
              <button
                onClick={() => openListingForm()}
                className={`h-9 px-4 rounded-lg text-[13px] font-medium inline-flex items-center gap-2 transition-colors ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
              >
                <Plus className="h-3.5 w-3.5" /> Eerste vacature aanmaken
              </button>
            </div>
          ) : (
            <>
              {/* Mobile compact cards */}
              <div className="sm:hidden space-y-3">
                {listings.map((listing, i) => (
                  <motion.div
                    key={listing.id}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.02 }}
                    className={`rounded-xl overflow-hidden ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
                  >
                    <div className="p-3 flex items-center gap-3">
                      {listing.media_url && !isVideo(listing.media_url) ? (
                        <img
                          src={getMediaUrl(listing.media_url)}
                          alt={listing.title}
                          className={`h-11 w-11 rounded-lg object-cover flex-shrink-0 border ${isDark ? "border-white/[0.06]" : "border-gray-200"}`}
                        />
                      ) : (
                        <div
                          className={`h-11 w-11 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
                        >
                          <Briefcase
                            className={`h-5 w-5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3
                            className={`text-[13px] font-semibold truncate ${isDark ? "text-white" : "text-gray-900"}`}
                          >
                            {listing.title}
                          </h3>
                          <span
                            className={`px-2 py-0.5 rounded-full text-[11px] font-medium flex-shrink-0 ${listing.is_active ? (isDark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-50 text-emerald-700") : isDark ? "bg-white/[0.06] text-gray-500" : "bg-gray-100 text-gray-600"}`}
                          >
                            {listing.is_active ? "Actief" : "Inactief"}
                          </span>
                        </div>
                        <span
                          className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                        >
                          {listing.type || "Full-Time"}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => openListingForm(listing)}
                          className={`h-8 w-8 rounded-lg inline-flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"}`}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setDeleteConfirmId(listing.id);
                            setDeleteType("listing");
                          }}
                          className={`h-8 w-8 rounded-lg inline-flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-red-400 hover:bg-red-500/10" : "text-gray-400 hover:text-red-600 hover:bg-red-50"}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Desktop detailed cards */}
              <div className="hidden sm:block space-y-2">
                {listings.map((listing) => (
                  <div
                    key={listing.id}
                    className={`rounded-xl p-4 transition-colors ${isDark ? "bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.05]" : "bg-white border border-gray-200/80 hover:border-gray-300"}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        {listing.media_url && (
                          <div className="flex-shrink-0">
                            {isVideo(listing.media_url) ? (
                              <div
                                className={`relative h-14 w-14 rounded-lg overflow-hidden flex items-center justify-center ${isDark ? "bg-white/[0.04] border border-white/[0.06]" : "bg-gray-100 border border-gray-200"}`}
                              >
                                <Video
                                  className={`h-5 w-5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                                />
                              </div>
                            ) : (
                              <img
                                src={getMediaUrl(listing.media_url)}
                                alt={listing.title}
                                className={`h-14 w-14 rounded-lg object-cover border ${isDark ? "border-white/[0.06]" : "border-gray-200"}`}
                              />
                            )}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                            <h3
                              className={`text-[14px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                            >
                              {listing.title}
                            </h3>
                            <span
                              className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${listing.is_active ? (isDark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-50 text-emerald-700") : isDark ? "bg-white/[0.06] text-gray-500" : "bg-gray-100 text-gray-600"}`}
                            >
                              {listing.is_active ? "Actief" : "Inactief"}
                            </span>
                            <span
                              className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${isDark ? "bg-white/[0.06] text-gray-400" : "bg-gray-100 text-gray-600"}`}
                            >
                              {listing.type || "Full-Time"}
                            </span>
                          </div>
                          {listing.short_description && (
                            <p
                              className={`text-[13px] line-clamp-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                            >
                              {listing.short_description}
                            </p>
                          )}
                          <p
                            className={`text-[11px] mt-0.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                          >
                            Aangemaakt op {formatDateShort(listing.created_at)}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleToggleListingActive(listing)}
                          title={listing.is_active ? "Deactiveer" : "Activeer"}
                          className={`h-7 w-7 rounded-lg inline-flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"}`}
                        >
                          {listing.is_active ? (
                            <ToggleRight className="h-4 w-4 text-emerald-500" />
                          ) : (
                            <ToggleLeft className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={() => openListingForm(listing)}
                          className={`h-7 w-7 rounded-lg inline-flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"}`}
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => {
                            setDeleteConfirmId(listing.id);
                            setDeleteType("listing");
                          }}
                          className={`h-7 w-7 rounded-lg inline-flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-red-400 hover:bg-red-500/10" : "text-gray-400 hover:text-red-600 hover:bg-red-50"}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </motion.div>
      )}

      {/* ─── Application Detail Dialog ────────────────────── */}
      <AnimatePresence>
        {detailOpen && (
          <div
            className={`fixed inset-0 z-50 flex ${isMobile ? "items-end" : "items-center"} justify-center ${isMobile ? "" : "p-4"}`}
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setDetailOpen(false)}
            />
            <motion.div
              ref={sheetRef}
              initial={isMobile ? { y: "100%" } : { scale: 0.95, opacity: 0 }}
              animate={isMobile ? { y: 0 } : { scale: 1, opacity: 1 }}
              exit={isMobile ? { y: "100%" } : { scale: 0.95, opacity: 0 }}
              transition={
                isMobile
                  ? { type: "spring", damping: 35, stiffness: 400 }
                  : { type: "spring", duration: 0.3 }
              }
              drag={isMobile ? "y" : false}
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0, bottom: 0.6 }}
              dragListener={false}
              dragControls={sheetDragControls}
              onDragEnd={(_, info) => {
                if (info.offset.y > 80 || info.velocity.y > 500)
                  setDetailOpen(false);
              }}
              style={
                isMobile
                  ? {
                      y: sheetY,
                      borderTopLeftRadius: "1rem",
                      borderTopRightRadius: "1rem",
                      borderBottomLeftRadius: 0,
                      borderBottomRightRadius: 0,
                    }
                  : undefined
              }
              className={`relative w-full ${isMobile ? "max-w-full max-h-[85dvh] flex flex-col" : "rounded-xl max-w-2xl mx-4"} ${isDark ? "bg-[#141414] border border-white/[0.06]" : "bg-white border border-gray-200"}`}
            >
              {isMobile && (
                <div
                  className="flex justify-center pt-3 pb-1 cursor-grab active:cursor-grabbing touch-none"
                  onPointerDown={(e) => sheetDragControls.start(e)}
                >
                  <div
                    className={`w-8 h-1 rounded-full ${isDark ? "bg-white/[0.15]" : "bg-gray-300"}`}
                  />
                </div>
              )}
              <div
                className={
                  isMobile
                    ? "p-6 overflow-y-auto overscroll-contain flex-1"
                    : "p-6"
                }
                ref={isMobile ? sheetContentRef : undefined}
              >
                {selectedApplication && (
                  <ApplicationDetail
                    application={selectedApplication}
                    isDark={isDark}
                    onStatusChange={handleStatusChange}
                    onRatingChange={handleRatingChange}
                    onNotesChange={handleNotesChange}
                    onClose={() => setDetailOpen(false)}
                  />
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ─── Listing Form Dialog ──────────────────────────── */}
      <AnimatePresence>
        {listingDialogOpen && (
          <div
            className={`fixed inset-0 z-50 flex ${isMobile ? "items-end" : "items-center"} justify-center ${isMobile ? "" : "p-4"}`}
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setListingDialogOpen(false)}
            />
            <motion.div
              ref={sheetRef}
              initial={isMobile ? { y: "100%" } : { scale: 0.95, opacity: 0 }}
              animate={isMobile ? { y: 0 } : { scale: 1, opacity: 1 }}
              exit={isMobile ? { y: "100%" } : { scale: 0.95, opacity: 0 }}
              transition={
                isMobile
                  ? { type: "spring", damping: 35, stiffness: 400 }
                  : { type: "spring", duration: 0.3 }
              }
              drag={isMobile ? "y" : false}
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0, bottom: 0.6 }}
              dragListener={false}
              dragControls={sheetDragControls}
              onDragEnd={(_, info) => {
                if (info.offset.y > 80 || info.velocity.y > 500)
                  setListingDialogOpen(false);
              }}
              style={
                isMobile
                  ? {
                      y: sheetY,
                      borderTopLeftRadius: "1rem",
                      borderTopRightRadius: "1rem",
                      borderBottomLeftRadius: 0,
                      borderBottomRightRadius: 0,
                    }
                  : undefined
              }
              className={`relative w-full ${isMobile ? "max-w-full max-h-[85dvh] flex flex-col" : "rounded-xl max-w-lg mx-4"} ${isDark ? "bg-[#141414] border border-white/[0.06]" : "bg-white border border-gray-200"}`}
            >
              {isMobile && (
                <div
                  className="flex justify-center pt-3 pb-1 cursor-grab active:cursor-grabbing touch-none"
                  onPointerDown={(e) => sheetDragControls.start(e)}
                >
                  <div
                    className={`w-8 h-1 rounded-full ${isDark ? "bg-white/[0.15]" : "bg-gray-300"}`}
                  />
                </div>
              )}
              <div
                className={
                  isMobile
                    ? "p-6 overflow-y-auto overscroll-contain flex-1"
                    : "p-6"
                }
                ref={isMobile ? sheetContentRef : undefined}
              >
                <div className="mb-5">
                  <div className="flex items-center justify-between mb-1">
                    <h2
                      className={`text-[14px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                    >
                      {editingListing ? "Vacature bewerken" : "Nieuwe vacature"}
                    </h2>
                    <button
                      onClick={() => setListingDialogOpen(false)}
                      className={`h-7 w-7 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <p
                    className={`text-[13px] mt-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    {editingListing
                      ? "Wijzig de gegevens van deze vacature."
                      : "Vul de details in voor de nieuwe vacature."}
                  </p>
                </div>
                <form onSubmit={handleListingSubmit}>
                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      <label
                        className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                      >
                        Titel *
                      </label>
                      <input
                        value={listingForm.title}
                        onChange={(e) =>
                          setListingForm({
                            ...listingForm,
                            title: e.target.value,
                          })
                        }
                        placeholder="bijv. Frontend Developer"
                        required
                        className={`w-full h-9 px-3 rounded-lg text-[13px] outline-none transition-colors ${isDark ? "bg-white/[0.04] border border-white/[0.06] text-white placeholder-gray-600 focus:border-white/[0.12]" : "bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label
                        className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                      >
                        Type
                      </label>
                      <Select
                        value={listingForm.type}
                        onValueChange={(v) =>
                          setListingForm({ ...listingForm, type: v })
                        }
                      >
                        <SelectTrigger
                          className={`h-9 rounded-lg text-[13px] ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-gray-50 border-gray-200 text-gray-900"}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent
                          className={
                            isDark ? "bg-[#1a1a1a] border-white/[0.08]" : ""
                          }
                        >
                          {JOB_TYPES.map((t) => (
                            <SelectItem key={t} value={t}>
                              {t}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <label
                        className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                      >
                        Korte beschrijving
                      </label>
                      <input
                        value={listingForm.shortDescription}
                        onChange={(e) =>
                          setListingForm({
                            ...listingForm,
                            shortDescription: e.target.value,
                          })
                        }
                        placeholder="Eén zin samenvatting"
                        className={`w-full h-9 px-3 rounded-lg text-[13px] outline-none transition-colors ${isDark ? "bg-white/[0.04] border border-white/[0.06] text-white placeholder-gray-600 focus:border-white/[0.12]" : "bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label
                        className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                      >
                        Uitgebreide beschrijving
                      </label>
                      <textarea
                        value={listingForm.longDescription}
                        onChange={(e) =>
                          setListingForm({
                            ...listingForm,
                            longDescription: e.target.value,
                          })
                        }
                        placeholder="Volledige functieomschrijving, vereisten, etc."
                        rows={5}
                        className={`w-full px-3 py-2 rounded-lg text-[13px] outline-none transition-colors resize-y ${isDark ? "bg-white/[0.04] border border-white/[0.06] text-white placeholder-gray-600 focus:border-white/[0.12]" : "bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label
                        className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                      >
                        Afbeelding of video
                      </label>
                      <input
                        ref={mediaInputRef}
                        type="file"
                        accept="image/*,video/mp4,video/webm,video/mov"
                        onChange={handleMediaUpload}
                        className="hidden"
                      />
                      {listingForm.mediaUrl ? (
                        <div className="relative w-fit">
                          {isVideo(listingForm.mediaUrl) ? (
                            <video
                              src={getMediaUrl(listingForm.mediaUrl)}
                              className={`h-36 max-w-xs rounded-lg object-cover border ${isDark ? "border-white/[0.06]" : "border-gray-200"}`}
                              controls
                              muted
                            />
                          ) : (
                            <img
                              src={getMediaUrl(listingForm.mediaUrl)}
                              alt="Vacature media"
                              className={`h-36 max-w-xs rounded-lg object-cover border ${isDark ? "border-white/[0.06]" : "border-gray-200"}`}
                            />
                          )}
                          <button
                            type="button"
                            onClick={() =>
                              setListingForm({
                                ...listingForm,
                                mediaUrl: "",
                              })
                            }
                            className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => mediaInputRef.current?.click()}
                          disabled={uploading}
                          className={`flex items-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg text-[13px] transition-colors w-full justify-center ${isDark ? "border-white/[0.08] text-gray-500 hover:border-white/[0.15] hover:text-gray-300" : "border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700"}`}
                        >
                          {uploading ? (
                            <>
                              <div
                                className={`w-4 h-4 rounded-full border-2 animate-spin ${isDark ? "border-white/[0.08] border-t-white/40" : "border-gray-200 border-t-gray-600"}`}
                              />
                              Uploaden...
                            </>
                          ) : (
                            <>
                              <Upload className="h-4 w-4" />
                              <span>Afbeelding of video uploaden</span>
                            </>
                          )}
                        </button>
                      )}
                      <p
                        className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                      >
                        Accepteert afbeeldingen (JPG, PNG, WebP) en video's
                        (MP4, WebM)
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="isActive"
                        checked={listingForm.isActive}
                        onCheckedChange={(checked) =>
                          setListingForm({
                            ...listingForm,
                            isActive: !!checked,
                          })
                        }
                      />
                      <label
                        htmlFor="isActive"
                        className={`text-[13px] cursor-pointer ${isDark ? "text-gray-400" : "text-gray-700"}`}
                      >
                        Actief (zichtbaar op de website)
                      </label>
                    </div>
                  </div>
                  <div
                    className={`flex justify-end gap-2 mt-6 pt-4 border-t ${isDark ? "border-white/[0.06]" : "border-gray-200"}`}
                  >
                    <button
                      type="button"
                      onClick={() => setListingDialogOpen(false)}
                      className={`h-9 px-4 rounded-lg text-[13px] font-medium border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                    >
                      Annuleren
                    </button>
                    <button
                      type="submit"
                      className={`h-9 px-4 rounded-lg text-[13px] font-medium transition-colors ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
                    >
                      {editingListing ? "Opslaan" : "Aanmaken"}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ─── Delete Confirm Dialog ────────────────────────── */}
      <AnimatePresence>
        {deleteConfirmId && (
          <div
            className={`fixed inset-0 z-50 flex ${isMobile ? "items-end" : "items-center"} justify-center ${isMobile ? "" : "p-4"}`}
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => {
                setDeleteConfirmId(null);
                setDeleteType(null);
              }}
            />
            <motion.div
              initial={isMobile ? { y: "100%" } : { scale: 0.95, opacity: 0 }}
              animate={isMobile ? { y: 0 } : { scale: 1, opacity: 1 }}
              exit={isMobile ? { y: "100%" } : { scale: 0.95, opacity: 0 }}
              transition={
                isMobile
                  ? { type: "spring", damping: 35, stiffness: 400 }
                  : { type: "spring", duration: 0.3 }
              }
              drag={isMobile ? "y" : false}
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0, bottom: 0.6 }}
              dragListener={false}
              dragControls={sheetDragControls}
              onDragEnd={(_, info) => {
                if (info.offset.y > 80 || info.velocity.y > 500) {
                  setDeleteConfirmId(null);
                  setDeleteType(null);
                }
              }}
              style={
                isMobile
                  ? {
                      y: sheetY,
                      borderTopLeftRadius: "1rem",
                      borderTopRightRadius: "1rem",
                      borderBottomLeftRadius: 0,
                      borderBottomRightRadius: 0,
                    }
                  : undefined
              }
              className={`relative w-full ${isMobile ? "max-w-full" : "rounded-xl max-w-sm mx-4"} ${isDark ? "bg-[#141414] border border-white/[0.06]" : "bg-white border border-gray-200"}`}
            >
              {isMobile && (
                <div
                  className="flex justify-center pt-3 pb-1 cursor-grab active:cursor-grabbing touch-none"
                  onPointerDown={(e) => sheetDragControls.start(e)}
                >
                  <div
                    className={`w-8 h-1 rounded-full ${isDark ? "bg-white/[0.15]" : "bg-gray-300"}`}
                  />
                </div>
              )}
              <div className="p-6">
                <h2
                  className={`text-[14px] font-semibold mb-2 ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  Bevestig verwijdering
                </h2>
                <p
                  className={`text-[13px] mb-6 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                >
                  {deleteType === "application"
                    ? "Weet je zeker dat je deze sollicitatie wilt verwijderen? Dit kan niet ongedaan worden gemaakt."
                    : "Weet je zeker dat je deze vacature wilt verwijderen? Dit kan niet ongedaan worden gemaakt."}
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => {
                      setDeleteConfirmId(null);
                      setDeleteType(null);
                    }}
                    className={`h-9 px-4 rounded-lg text-[13px] font-medium border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                  >
                    Annuleren
                  </button>
                  <button
                    onClick={() =>
                      deleteType === "application"
                        ? handleDeleteApplication(deleteConfirmId)
                        : handleDeleteListing(deleteConfirmId)
                    }
                    className="h-9 px-4 rounded-lg text-[13px] font-medium bg-red-500 text-white hover:bg-red-600 transition-colors"
                  >
                    Verwijderen
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Application Card Sub-component ───────────────────────

const ApplicationCard = ({
  application,
  isDark,
  onStatusChange,
  onRatingChange,
  onView,
  onDelete,
}) => {
  const STATUS_CONFIG = getStatusConfig(isDark);
  const statusCfg = STATUS_CONFIG[application.status] || STATUS_CONFIG.new;

  return (
    <div
      className={`rounded-xl p-4 transition-colors ${isDark ? "bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.05]" : "bg-white border border-gray-200/80 hover:border-gray-300"}`}
    >
      <div className="flex items-start justify-between gap-4">
        {/* Left: Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3
              className={`text-[14px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
            >
              {application.first_name} {application.last_name}
            </h3>
            <span
              className={`${statusCfg.color} px-2 py-0.5 rounded-full text-[11px] font-medium inline-flex items-center gap-1`}
            >
              {statusCfg.icon}
              {statusCfg.label}
            </span>
          </div>

          <div
            className={`flex items-center gap-3 text-[13px] mb-2 flex-wrap ${isDark ? "text-gray-500" : "text-gray-500"}`}
          >
            <span className="flex items-center gap-1">
              <Briefcase className="h-3 w-3" />
              {application.job_title}
            </span>
            <span className="flex items-center gap-1">
              <Mail className="h-3 w-3" />
              {application.email}
            </span>
            {application.phone && (
              <span className="flex items-center gap-1">
                <Phone className="h-3 w-3" />
                {application.phone}
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            <StarRating
              rating={application.rating || 0}
              onRate={(r) => onRatingChange(application.id, r)}
            />
            <span
              className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
            >
              {formatDateShort(application.created_at)}
            </span>
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-1 shrink-0">
          <Select
            value={application.status}
            onValueChange={(v) => onStatusChange(application.id, v)}
          >
            <SelectTrigger
              className={`w-[130px] h-8 text-[11px] rounded-lg ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-gray-50 border-gray-200 text-gray-900"}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent
              className={isDark ? "bg-[#1a1a1a] border-white/[0.08]" : ""}
            >
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <SelectItem key={key} value={key}>
                  <span className="flex items-center gap-1">
                    {cfg.icon} {cfg.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <button
            onClick={onView}
            className={`h-7 w-7 rounded-lg inline-flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"}`}
          >
            <Eye className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={onDelete}
            className={`h-7 w-7 rounded-lg inline-flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-red-400 hover:bg-red-500/10" : "text-gray-400 hover:text-red-600 hover:bg-red-50"}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Application Detail Sub-component ─────────────────────

const ApplicationDetail = ({
  application,
  isDark,
  onStatusChange,
  onRatingChange,
  onNotesChange,
}) => {
  const [notes, setNotes] = useState(application.notes || "");
  const [notesChanged, setNotesChanged] = useState(false);
  const STATUS_CONFIG = getStatusConfig(isDark);
  const statusCfg = STATUS_CONFIG[application.status] || STATUS_CONFIG.new;

  const handleSaveNotes = () => {
    onNotesChange(application.id, notes);
    setNotesChanged(false);
  };

  // Parse attachments
  let attachments = [];
  try {
    attachments =
      typeof application.attachments === "string"
        ? JSON.parse(application.attachments)
        : application.attachments || [];
  } catch {
    attachments = [];
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <h2
            className={`text-[15px] font-semibold tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}
          >
            {application.first_name} {application.last_name}
          </h2>
          <span
            className={`${statusCfg.color} px-2 py-0.5 rounded-full text-[11px] font-medium inline-flex items-center gap-1`}
          >
            {statusCfg.icon}
            {statusCfg.label}
          </span>
        </div>
        <p
          className={`text-[13px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
        >
          Sollicitatie voor:{" "}
          <span className="font-medium">{application.job_title}</span> ·{" "}
          {formatDate(application.created_at)}
        </p>
      </div>

      {/* Contact Info */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div
          className={`flex items-center gap-2 text-[13px] ${isDark ? "text-gray-400" : "text-gray-600"}`}
        >
          <Mail
            className={`h-3.5 w-3.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
          />
          <a
            href={`mailto:${application.email}`}
            className={`hover:underline ${isDark ? "text-blue-400" : "text-blue-600"}`}
          >
            {application.email}
          </a>
        </div>
        {application.phone && (
          <div
            className={`flex items-center gap-2 text-[13px] ${isDark ? "text-gray-400" : "text-gray-600"}`}
          >
            <Phone
              className={`h-3.5 w-3.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
            />
            <a
              href={`tel:${application.phone}`}
              className={`hover:underline ${isDark ? "text-blue-400" : "text-blue-600"}`}
            >
              {application.phone}
            </a>
          </div>
        )}
        {application.portfolio && (
          <div
            className={`flex items-center gap-2 text-[13px] ${isDark ? "text-gray-400" : "text-gray-600"}`}
          >
            <Globe
              className={`h-3.5 w-3.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
            />
            <a
              href={
                application.portfolio.startsWith("http")
                  ? application.portfolio
                  : `https://${application.portfolio}`
              }
              target="_blank"
              rel="noopener noreferrer"
              className={`hover:underline truncate ${isDark ? "text-blue-400" : "text-blue-600"}`}
            >
              {application.portfolio}
            </a>
          </div>
        )}
        {application.type && (
          <div className={`flex items-center gap-2 text-[13px]`}>
            <Briefcase
              className={`h-3.5 w-3.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
            />
            <span className={isDark ? "text-gray-400" : "text-gray-700"}>
              {application.type}
            </span>
          </div>
        )}
      </div>

      {/* Motivation */}
      {application.message && (
        <div>
          <h4
            className={`text-[11px] font-medium uppercase tracking-wider mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
          >
            Motivatie
          </h4>
          <div
            className={`rounded-lg p-3 text-[13px] whitespace-pre-wrap max-h-48 overflow-y-auto ${isDark ? "bg-white/[0.04] text-gray-300" : "bg-gray-50 text-gray-700"}`}
          >
            {application.message}
          </div>
        </div>
      )}

      {/* Attachments */}
      {attachments.length > 0 && (
        <div>
          <h4
            className={`text-[11px] font-medium uppercase tracking-wider mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
          >
            Bijlagen
          </h4>
          <div className="flex flex-wrap gap-2">
            {attachments.map((att, i) => {
              const url = att.s3Key
                ? `${S3_BASE_URL}/${att.s3Key}`
                : att.content || null;
              return (
                <a
                  key={i}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] transition-colors group ${isDark ? "bg-white/[0.04] hover:bg-white/[0.08]" : "bg-gray-50 hover:bg-gray-100"}`}
                >
                  <FileText
                    className={`h-3 w-3 ${isDark ? "text-gray-600 group-hover:text-blue-400" : "text-gray-400 group-hover:text-blue-600"}`}
                  />
                  <span className={isDark ? "text-blue-400" : "text-blue-600"}>
                    {att.filename}
                  </span>
                  <ExternalLink
                    className={`h-2.5 w-2.5 ${isDark ? "text-gray-600 group-hover:text-blue-400" : "text-gray-300 group-hover:text-blue-600"}`}
                  />
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* Status + Rating */}
      <div className="flex items-center gap-4 flex-wrap">
        <div>
          <label
            className={`text-[11px] font-medium uppercase tracking-wider block mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
          >
            Status
          </label>
          <Select
            value={application.status}
            onValueChange={(v) => onStatusChange(application.id, v)}
          >
            <SelectTrigger
              className={`w-[160px] h-9 rounded-lg text-[13px] ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-gray-50 border-gray-200 text-gray-900"}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent
              className={isDark ? "bg-[#1a1a1a] border-white/[0.08]" : ""}
            >
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <SelectItem key={key} value={key}>
                  <span className="flex items-center gap-1">
                    {cfg.icon} {cfg.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label
            className={`text-[11px] font-medium uppercase tracking-wider block mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
          >
            Beoordeling
          </label>
          <StarRating
            rating={application.rating || 0}
            onRate={(r) => onRatingChange(application.id, r)}
            size="md"
          />
        </div>
      </div>

      {/* Notes */}
      <div>
        <label
          className={`text-[11px] font-medium uppercase tracking-wider block mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
        >
          Interne notities
        </label>
        <textarea
          value={notes}
          onChange={(e) => {
            setNotes(e.target.value);
            setNotesChanged(true);
          }}
          placeholder="Voeg interne notities toe over deze kandidaat..."
          rows={3}
          className={`w-full px-3 py-2 rounded-lg text-[13px] outline-none transition-colors resize-y ${isDark ? "bg-white/[0.04] border border-white/[0.06] text-white placeholder-gray-600 focus:border-white/[0.12]" : "bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
        />
        {notesChanged && (
          <button
            onClick={handleSaveNotes}
            className={`mt-2 h-8 px-3 rounded-lg text-[12px] font-medium transition-colors ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
          >
            Notities opslaan
          </button>
        )}
      </div>
    </div>
  );
};

export default JobApplicationsManager;
